import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { useCallback } from 'react';

interface DocumentoDetalhesModalProps {
  open: boolean;
  onClose: () => void;
  documento: any;
}

const DocumentoDetalhesModal = ({ open, onClose, documento }: DocumentoDetalhesModalProps) => {
  const handleDownload = useCallback((url: string) => {
    window.open(url, '_blank');
  }, []);

  if (!documento) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalhes do Documento</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Informações Básicas */}
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4">Informações do Documento</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600">Modelo</div>
                <div className="font-medium">{documento.DocModelo}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Número</div>
                <div className="font-medium">{documento.DocNumero}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Série</div>
                <div className="font-medium">{documento.DocSerie}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Protocolo</div>
                <div className="font-medium">{documento.DocProtocolo || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Situação</div>
                <div className="font-medium">
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      documento.DocSitCodigo === 100
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {documento.DocSitDescricao}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Data Emissão</div>
                <div className="font-medium">{documento.Resumo?.DocDataEmissao || '-'}</div>
              </div>
            </div>
          </Card>

          {/* Emitente e Destinatário */}
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4">Partes Envolvidas</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-sm text-gray-600 mb-2">Emitente</h4>
                <div className="space-y-1">
                  <div className="text-sm">
                    <strong>Nome:</strong> {documento.Resumo?.DocNomeEmitente || '-'}
                  </div>
                  <div className="text-sm">
                    <strong>CNPJ:</strong> {documento.Resumo?.DocCNPJEmitente || '-'}
                  </div>
                  <div className="text-sm">
                    <strong>UF:</strong> {documento.Resumo?.DocUFEmitente || '-'}
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-sm text-gray-600 mb-2">Destinatário</h4>
                <div className="space-y-1">
                  <div className="text-sm">
                    <strong>Nome:</strong> {documento.Resumo?.DocNomeDestinatario || '-'}
                  </div>
                  <div className="text-sm">
                    <strong>CNPJ:</strong> {documento.Resumo?.DocCNPJDestinatario || '-'}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Valores */}
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4">Valores</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-sm text-gray-600">Valor Total</div>
                <div className="text-2xl font-bold">R$ {documento.Resumo?.DocVlrTotal || '0,00'}</div>
              </div>
            </div>
          </Card>

          {/* Chave de Acesso */}
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4">Chave de Acesso</h3>
            <div className="font-mono text-sm bg-gray-100 p-3 rounded break-all">
              {documento.DocChaAcesso}
            </div>
          </Card>

          {/* Downloads */}
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4">Downloads</h3>
            <div className="flex gap-3">
              {documento.DocXMLLink && (
                <Button
                  variant="outline"
                  onClick={() => handleDownload(documento.DocXMLLink)}
                  className="flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  XML
                </Button>
              )}
              {documento.DocPDFLink && (
                <Button
                  variant="outline"
                  onClick={() => handleDownload(documento.DocPDFLink)}
                  className="flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  PDF
                </Button>
              )}
              {documento.DocXML && (
                <Button
                  variant="outline"
                  onClick={() => {
                    const blob = new Blob([documento.DocXML], { type: 'application/xml' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${documento.DocChaAcesso}.xml`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  XML Completo
                </Button>
              )}
            </div>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentoDetalhesModal;
